import React, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import FieldRenderer from './FieldRenderer'
import LocationConsentModal from './LocationConsentModal'
import { getFormFiles, rePopulateFormFields } from 'services/taskService'
import { setLocationConsent as setLocationConsentApi } from 'services/AuthService'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { fetchAuthUserRoutine } from 'features/auth/ducks/actions'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'

const toSnakeCase = str => str.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`)
const snakeCaseResponseKeys = obj => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    result[toSnakeCase(key)] = value
  }
  return result
}

const toConditionList = value => {
  if (Array.isArray(value)) {
    return value.map(entry => String(entry).trim()).filter(entry => entry !== '')
  }
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').map(entry => entry.trim()).filter(entry => entry !== '')
}

const conditionListContains = (listValue, fieldValue) => {
  const list = toConditionList(listValue)
  if (list.length === 0) return false
  return toConditionList(fieldValue).some(entry => list.includes(entry))
}

const numericCompare = (a, b, cmp) => {
  const x = a === '' || a === null || a === undefined ? NaN : Number(a)
  const y = b === '' || b === null || b === undefined ? NaN : Number(b)
  return isFinite(x) && isFinite(y) && cmp(x, y)
}

const evaluateConditional = (conditional, responses) => {
  const fieldValue = responses[conditional.fieldKey]

  switch (conditional.operator) {
    case 'equals':
      return fieldValue === conditional.value
    case 'not_equals':
      return fieldValue !== conditional.value
    case 'greater_than':
      return numericCompare(fieldValue, conditional.value, (a, b) => a > b)
    case 'greater_or_equal':
      return numericCompare(fieldValue, conditional.value, (a, b) => a >= b)
    case 'less_than':
      return numericCompare(fieldValue, conditional.value, (a, b) => a < b)
    case 'less_or_equal':
      return numericCompare(fieldValue, conditional.value, (a, b) => a <= b)
    case 'in':
      return conditionListContains(conditional.value, fieldValue)
    case 'not_in':
      return !conditionListContains(conditional.value, fieldValue)
    case 'is_empty':
      return (
        (!fieldValue && fieldValue !== 0 && fieldValue !== '0') ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      )
    case 'is_not_empty':
      return (
        (!!fieldValue || fieldValue === 0 || fieldValue === '0') &&
        (!Array.isArray(fieldValue) || fieldValue.length > 0)
      )
    default:
      return true
  }
}

const isFieldVisible = (field, responses) => {
  if (!field.conditionals || field.conditionals.length === 0) return true

  const method = field.conditionLogic === 'or' ? 'some' : 'every'
  return field.conditionals[method](cond => {
    const result = evaluateConditional(cond, responses)
    if (cond.action === 'hide') return !result
    if (cond.action === 'show') return result
    return true
  })
}

const isFieldRequired = (field, responses) => {
  if (!field.conditionals) return !!field.required

  const conditionalRequire = field.conditionals.find(
    c => c.action === 'require'
  )
  if (conditionalRequire) {
    return evaluateConditional(conditionalRequire, responses)
  }

  return !!field.required
}

const isSectionVisible = (section, responses) => {
  if (!section.conditionals || section.conditionals.length === 0) return true

  const method = section.conditionLogic === 'or' ? 'some' : 'every'
  return section.conditionals[method](cond => {
    const result = evaluateConditional(cond, responses)
    if (cond.action === 'hide') return !result
    if (cond.action === 'show') return result
    return true
  })
}

const isFieldFilled = (field, value) => {
  if (field.type === 'file') {
    return !!(value && typeof value === 'object' && value.fileId)
  }
  if (field.type === 'checklist_upload') {
    if (value && typeof value === 'object') {
      return value.completed === true
    }
    return value === true
  }
  if (field.type === 'checkbox') {
    return value === true
  }
  if (field.type === 'audit_question' || field.type === 'scored_question') {
    return !!(value && value.response)
  }
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}

const FIRST_MISSING_NOTE = 'Answer this question first. Your task cannot be submitted until it has an answer.'

export const fieldDomId = (sectionKey, entryIndex, fieldKey) =>
  entryIndex === null || entryIndex === undefined ? fieldKey : `${sectionKey}_${entryIndex}_${fieldKey}`

export const requiredSummary = (schema, responses = {}) => {
  let total = 0
  let answered = 0
  let firstMissingId = null

  const count = (field, value, id) => {
    total++
    if (isFieldFilled(field, value)) {
      answered++
    } else if (firstMissingId === null) {
      firstMissingId = id
    }
  }

  for (const section of schema?.sections || []) {
    if (!isSectionVisible(section, responses)) continue

    if (section.repeatable) {
      const entries = Array.isArray(responses[section.key])
        ? responses[section.key]
        : [{}]
      const minEntries = section.minEntries ?? 0

      for (let i = 0; i < Math.max(entries.length, minEntries); i++) {
        const entry = entries[i] || {}
        const entryResponses = { ...responses, ...entry }
        for (const field of section.fields || []) {
          if (!isFieldVisible(field, entryResponses)) continue
          if (!isFieldRequired(field, entryResponses)) continue
          count(field, entry[field.key], fieldDomId(section.key, i, field.key))
        }
      }
    } else {
      for (const field of section.fields || []) {
        if (!isFieldVisible(field, responses)) continue
        if (!isFieldRequired(field, responses)) continue
        count(field, responses[field.key], fieldDomId(section.key, null, field.key))
      }
    }
  }

  return { total, answered, firstMissingId }
}

export const blockedMessage = remaining => (remaining === 1
  ? 'You cannot submit yet: 1 required question still needs an answer. We have taken you to it.'
  : `You cannot submit yet: ${remaining} required questions still need an answer. We have taken you to the first one.`)

const progressFromSummary = ({ total, answered }) =>
  total === 0 ? 100 : Math.round((answered / total) * 100)

const resolveTaskContextValue = (task, source) => {
  if (!task || !source) return ''
  switch (source) {
    case 'creator': {
      const c = task.creator
      return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : ''
    }
    case 'createdAt':
      return task.createdAt || ''
    case 'assignees':
      return (task.assignees || [])
        .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ')
    case 'submittedAt':
      return task.submittedAt || ''
    case 'approvedAt':
      return task.approvedAt || ''
    default:
      return ''
  }
}

const FormRenderer = forwardRef(({
  schema,
  responses: initialResponses = {},
  onSave,
  onSubmit,
  onAutoSave,
  lastSavedAt,
  isOnline = true,
  readOnly = false,
  saving = false,
  taskId,
  task,
  onTaskRefresh,
  autoPopulateKeys = [],
  showAllSections = false,
  onProgressChange,
  onSubmitBlocked
}, ref) => {
  const [responses, setResponses] = useState(initialResponses)
  const [lastSavedResponses, setLastSavedResponses] = useState(initialResponses)
  const pollRef = useRef(null)
  const autoSaveTimerRef = useRef(null)

  // Auto-fill task context fields (creator, assignees, dates) from the task object
  useEffect(() => {
    if (!task || !schema) return
    const updates = {}
    for (const section of schema.sections || []) {
      for (const field of section.fields || []) {
        if (field.taskContextSource) {
          const value = resolveTaskContextValue(task, field.taskContextSource)
          if (value && value !== responses[field.key]) {
            updates[field.key] = value
          }
        }
      }
    }
    if (Object.keys(updates).length > 0) {
      setResponses(prev => ({ ...prev, ...updates }))
    }
  }, [task, schema])

  const isDirty = useMemo(() => {
    return JSON.stringify(responses) !== JSON.stringify(lastSavedResponses)
  }, [responses, lastSavedResponses])

  // Auto-save effect with 5s debounce
  // Note: We call onAutoSave even when offline - parent handles queuing
  useEffect(() => {
    if (readOnly || !onAutoSave || !isDirty) {
      return
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      // Auto-save also stamps the location so the audit trail covers
      // every save event, not just the explicit Save / Submit clicks.
      const submissionLocation = await captureSubmissionLocation()
      onAutoSave(responses, { submissionLocation })
      // Only update lastSavedResponses if online (actual save happened)
      if (isOnline) {
        setLastSavedResponses(responses)
      }
    }, 5000)

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  }, [responses, isDirty, isOnline, readOnly, onAutoSave])

  // Sync lastSavedResponses when parent confirms save (via lastSavedAt update)
  const prevLastSavedAtRef = useRef(lastSavedAt)
  useEffect(() => {
    if (lastSavedAt && lastSavedAt !== prevLastSavedAtRef.current) {
      setLastSavedResponses(responses)
      prevLastSavedAtRef.current = lastSavedAt
    }
  }, [lastSavedAt, responses])

  // Beforeunload warning when dirty
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Load existing form files and poll for AI descriptions
  useEffect(() => {
    if (!taskId || !checkIsOnline()) return

    const loadFiles = async () => {
      try {
        const res = await getFormFiles(taskId)
        const files = res.data?.data || []
        if (files.length > 0) {
          setResponses(prev => {
            const updated = { ...prev }

            const refreshFileValue = (current) => {
              const fid = current?.fileId || current?.file_id
              if (!current || typeof current !== 'object' || !fid) return current
              const matchingFile = files.find(f => f.id === fid)
              if (!matchingFile) return current
              return {
                ...current,
                url: matchingFile.url || current.url,
                ...(matchingFile.description && !current.description
                  ? { description: matchingFile.description, descriptionGenerating: false }
                  : {})
              }
            }

            for (const section of schema?.sections || []) {
              if (section.repeatable) {
                const entries = updated[section.key]
                if (Array.isArray(entries)) {
                  updated[section.key] = entries.map(entry => {
                    if (!entry || typeof entry !== 'object') return entry
                    const refreshed = { ...entry }
                    for (const field of section.fields || []) {
                      if (field.type === 'file' && refreshed[field.key]) {
                        refreshed[field.key] = refreshFileValue(refreshed[field.key])
                      }
                    }
                    return refreshed
                  })
                }
              } else {
                for (const field of section.fields || []) {
                  if (field.type !== 'file') continue
                  updated[field.key] = refreshFileValue(updated[field.key])
                }
              }
            }
            return updated
          })
        }
      } catch (err) {
        // Silently fail — files will load when user uploads
      }
    }

    loadFiles()
  }, [taskId, schema])

  // Re-populate empty auto-populate fields on mount
  useEffect(() => {
    if (!taskId || !checkIsOnline() || readOnly) return

    const hasAutoPopulateFields = schema?.sections?.some(s =>
      s.fields?.some(f => f.autoPopulated && f.autoPopulateSource)
    )
    if (!hasAutoPopulateFields) return

    const rePopulate = async () => {
      try {
        const res = await rePopulateFormFields(taskId)
        const updates = snakeCaseResponseKeys(res.data?.data)
        if (updates && Object.keys(updates).length > 0) {
          setResponses(prev => {
            const merged = { ...prev }
            for (const [key, val] of Object.entries(updates)) {
              if (!merged[key] || merged[key] === '') {
                merged[key] = val
              }
            }
            return merged
          })
        }
      } catch (err) {
        // Silently fail — fields will use existing values
      }
    }

    rePopulate()
  }, [taskId, schema, readOnly])

  // Poll for descriptions when any file field has descriptionGenerating
  useEffect(() => {
    if (!taskId) return

    const hasGenerating = Object.values(responses).some(
      v => v && typeof v === 'object' && v.descriptionGenerating
    )

    if (hasGenerating && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await getFormFiles(taskId)
          const files = res.data?.data || []

          setResponses(prev => {
            const updated = { ...prev }
            let changed = false
            for (const [key, val] of Object.entries(updated)) {
              if (
                val &&
                typeof val === 'object' &&
                val.descriptionGenerating &&
                val.fileId
              ) {
                const matchingFile = files.find(f => f.id === val.fileId)
                if (matchingFile?.description) {
                  updated[key] = {
                    ...val,
                    description: matchingFile.description,
                    descriptionGenerating: false,
                    url: matchingFile.url || val.url
                  }
                  changed = true
                }
              }
            }
            return changed ? updated : prev
          })
        } catch (err) {
          // Polling error, will retry
        }
      }, 5000)
    } else if (!hasGenerating && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [taskId, responses])

  const handleFieldChange = useCallback((key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleCommentChange = useCallback((fieldKey, commentText) => {
    setResponses(prev => ({
      ...prev,
      _field_comments: {
        ...(prev._field_comments || {}),
        [fieldKey]: commentText
      }
    }))
  }, [])

  const handleAttachmentsChange = useCallback((fieldKey, files) => {
    setResponses(prev => ({
      ...prev,
      _field_attachments: {
        ...(prev._field_attachments || {}),
        [fieldKey]: files
      }
    }))
  }, [])

  const handleRepeatableAttachmentsChange = useCallback(
    (sectionKey, entryIndex, fieldKey, files) => {
      setResponses(prev => {
        const entries = Array.isArray(prev[sectionKey])
          ? [...prev[sectionKey]]
          : []
        if (!entries[entryIndex]) entries[entryIndex] = {}
        entries[entryIndex] = {
          ...entries[entryIndex],
          _field_attachments: {
            ...(entries[entryIndex]._field_attachments || {}),
            [fieldKey]: files
          }
        }
        return { ...prev, [sectionKey]: entries }
      })
    },
    []
  )

  const handleRepeatableChange = useCallback(
    (sectionKey, entryIndex, fieldKey, value) => {
      setResponses(prev => {
        const entries = Array.isArray(prev[sectionKey])
          ? [...prev[sectionKey]]
          : []
        if (!entries[entryIndex]) entries[entryIndex] = {}
        entries[entryIndex] = { ...entries[entryIndex], [fieldKey]: value }
        return { ...prev, [sectionKey]: entries }
      })
    },
    []
  )

  const handleRepeatableCommentChange = useCallback(
    (sectionKey, entryIndex, fieldKey, commentText) => {
      setResponses(prev => {
        const entries = Array.isArray(prev[sectionKey])
          ? [...prev[sectionKey]]
          : []
        if (!entries[entryIndex]) entries[entryIndex] = {}
        entries[entryIndex] = {
          ...entries[entryIndex],
          _field_comments: {
            ...(entries[entryIndex]._field_comments || {}),
            [fieldKey]: commentText
          }
        }
        return { ...prev, [sectionKey]: entries }
      })
    },
    []
  )

  const addRepeatableEntry = useCallback(sectionKey => {
    setResponses(prev => {
      const entries = Array.isArray(prev[sectionKey])
        ? [...prev[sectionKey]]
        : []
      entries.push({})
      return { ...prev, [sectionKey]: entries }
    })
  }, [])

  const removeRepeatableEntry = useCallback((sectionKey, index) => {
    setResponses(prev => {
      const entries = Array.isArray(prev[sectionKey])
        ? [...prev[sectionKey]]
        : []
      entries.splice(index, 1)
      return { ...prev, [sectionKey]: entries }
    })
  }, [])

  const autoPopulateKeysSet = useMemo(
    () => new Set(autoPopulateKeys),
    [autoPopulateKeys]
  )

  const summary = useMemo(
    () => requiredSummary(schema, responses),
    [schema, responses]
  )
  const progress = progressFromSummary(summary)
  const remaining = summary.total - summary.answered
  const containerRef = useRef(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const showMissing = (submitAttempted && remaining > 0) || (progress >= 80 && progress < 100)

  useEffect(() => {
    if (onProgressChange) onProgressChange({ answered: summary.answered, total: summary.total })
  }, [onProgressChange, summary.answered, summary.total])

  useEffect(() => {
    if (remaining === 0) {
      setSubmitAttempted(false)
      if (onSubmitBlocked) onSubmitBlocked(null)
    }
  }, [remaining, onSubmitBlocked])

  const focusField = id => {
    const root = containerRef.current
    if (!root) return false
    const nodes = Array.from(root.querySelectorAll('[data-field-id]'))
    const target = (id && nodes.find(n => n.getAttribute('data-field-id') === id)) || null
    if (!target) return false
    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.setTimeout(() => {
        const rect = target.getBoundingClientRect()
        const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        if (!inView) target.scrollIntoView({ block: 'center' })
      }, 700)
    }
    const input = target.querySelector('input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])')
    if (input && typeof input.focus === 'function') input.focus({ preventScroll: true })
    return true
  }

  const scrollToFirstQuestion = () => {
    if (focusField(summary.firstMissingId)) return true
    const first = containerRef.current?.querySelector('[data-field-id]')
    return first ? focusField(first.getAttribute('data-field-id')) : false
  }

  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const [consentModal, setConsentModal] = useState(null)

  const knownLocationConsent = currentUser?.locationConsent
    || (typeof localStorage !== 'undefined' ? localStorage.getItem('locationConsent') : null)

  const ensureLocationConsent = () => new Promise((resolve) => {
    if (knownLocationConsent === 'granted' || knownLocationConsent === 'declined') {
      resolve(knownLocationConsent)
      return
    }
    setConsentModal({ resolve })
  })

  const recordLocationConsent = async (choice) => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('locationConsent', choice)
    } catch (e) {}
    if (currentUser?.id) {
      try {
        await setLocationConsentApi(choice)
        dispatch(fetchAuthUserRoutine())
      } catch (e) {}
    }
    const pending = consentModal
    setConsentModal(null)
    pending?.resolve?.(choice)
  }

  const captureSubmissionLocation = (consent) => new Promise((resolve) => {
    if (consent !== 'granted') {
      resolve({ source: 'declined', captured_at: new Date().toISOString() })
      return
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ source: 'unavailable', captured_at: new Date().toISOString() })
      return
    }
    let settled = false
    const done = (loc) => { if (!settled) { settled = true; resolve(loc) } }
    setTimeout(() => done({ source: 'unavailable', captured_at: new Date().toISOString() }), 4000)
    navigator.geolocation.getCurrentPosition(
      (pos) => done({
        source: 'browser',
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy ? Math.round(pos.coords.accuracy) : null,
        captured_at: new Date().toISOString(),
      }),
      (err) => done({
        source: err && err.code === 1 ? 'denied' : 'unavailable',
        captured_at: new Date().toISOString(),
      }),
      { enableHighAccuracy: false, timeout: 3500, maximumAge: 60000 },
    )
  })

  const handleSave = async () => {
    if (!onSave) return
    const consent = await ensureLocationConsent()
    const submissionLocation = await captureSubmissionLocation(consent)
    onSave(responses, { submissionLocation })
    setLastSavedResponses(responses)
  }

  const handleSubmit = async () => {
    if (!onSubmit) return
    const consent = await ensureLocationConsent()
    const submissionLocation = await captureSubmissionLocation(consent)
    onSubmit(responses, { submissionLocation })
  }

  const requestSubmit = async () => {
    if (!onSubmit || readOnly) return false
    if (remaining > 0) {
      setSubmitAttempted(true)
      if (onSubmitBlocked) onSubmitBlocked(blockedMessage(remaining))
      focusField(summary.firstMissingId)
      return false
    }
    await handleSubmit()
    return true
  }

  useImperativeHandle(ref, () => ({ requestSubmit, scrollToFirstQuestion }))

  if (!schema || !schema.sections) {
    return <EmptyMessage>No form schema available.</EmptyMessage>
  }

  return (
    <FormContainer ref={containerRef}>
      {/* Progress Bar */}
      <ProgressSection>
        <ProgressHeader>
          <ProgressLabel>Progress</ProgressLabel>
          <ProgressValue $complete={progress === 100}>
            {progress}%
          </ProgressValue>
        </ProgressHeader>
        <ProgressTrack>
          <ProgressFill $progress={progress} $complete={progress === 100} />
        </ProgressTrack>
        {!readOnly && (
          <SaveStatus>
            {saving ? (
              <SaveStatusText>Saving…</SaveStatusText>
            ) : isDirty ? (
              <SaveStatusText $warning>Unsaved changes</SaveStatusText>
            ) : lastSavedAt ? (
              <SaveStatusText $success>
                Auto-saved at{' '}
                {new Date(lastSavedAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </SaveStatusText>
            ) : null}
          </SaveStatus>
        )}
      </ProgressSection>

      {/* Sections */}
      {schema.sections.map(section => {
        const sectionVisible = isSectionVisible(section, responses)
        if (!sectionVisible && !showAllSections) return null
        return (
          <SectionCard key={section.key} $conditionallyHidden={!sectionVisible}>
            <SectionTitle>
              {section.title || (showAllSections ? 'Untitled section' : null)}
              {showAllSections && !sectionVisible && (
                <ConditionalBadge>Only shown when its condition is met</ConditionalBadge>
              )}
            </SectionTitle>
            {section.description && (
              <SectionDescription>{section.description}</SectionDescription>
            )}

            {section.repeatable ? (
              <div>
                {(() => {
                  const existingEntries = Array.isArray(responses[section.key])
                    ? responses[section.key]
                    : []
                  const minEntries = section.minEntries ?? 1
                  // For minEntries: 0, allow empty; otherwise ensure minimum
                  const entries =
                    minEntries === 0
                      ? existingEntries
                      : existingEntries.length >= minEntries
                        ? existingEntries
                        : [
                            ...existingEntries,
                            ...Array(minEntries - existingEntries.length).fill(
                              {}
                            )
                          ]
                  return entries
                })().map((entry, entryIndex, arr) => (
                  <RepeatableEntry key={entryIndex}>
                    <RepeatableHeader>
                      <EntryLabel>Entry {entryIndex + 1}</EntryLabel>
                      {!readOnly && arr.length > (section.minEntries ?? 1) && (
                        <RemoveEntryBtn
                          type='button'
                          onClick={() =>
                            removeRepeatableEntry(section.key, entryIndex)
                          }
                        >
                          Remove
                        </RemoveEntryBtn>
                      )}
                    </RepeatableHeader>
                    {(section.fields || []).map(field => {
                      const entryResponses = { ...responses, ...entry }
                      if (!isFieldVisible(field, entryResponses)) return null
                      const isAutoPopulated = autoPopulateKeysSet.has(field.key)
                      return (
                        <FieldRenderer
                          key={field.key}
                          field={{
                            ...field,
                            required: isFieldRequired(field, entryResponses)
                          }}
                          value={entry?.[field.key]}
                          commentValue={entry?._field_comments?.[field.key]}
                          attachmentsValue={entry?._field_attachments?.[field.key]}
                          onAttachmentsChange={(fk, files) =>
                            handleRepeatableAttachmentsChange(
                              section.key,
                              entryIndex,
                              fk,
                              files
                            )
                          }
                          onChange={(key, val) =>
                            handleRepeatableChange(
                              section.key,
                              entryIndex,
                              key,
                              val
                            )
                          }
                          onCommentChange={(fk, text) =>
                            handleRepeatableCommentChange(
                              section.key,
                              entryIndex,
                              fk,
                              text
                            )
                          }
                          readOnly={readOnly || isAutoPopulated}
                          autoPopulated={isAutoPopulated}
                          taskId={taskId}
                          task={task}
                          onTaskRefresh={onTaskRefresh}
                          entryKey={`${section.key}_${entryIndex}`}
                          showMissing={showMissing}
                          missingNote={submitAttempted && summary.firstMissingId === fieldDomId(section.key, entryIndex, field.key) ? FIRST_MISSING_NOTE : null}
                        />
                      )
                    })}
                  </RepeatableEntry>
                ))}
                {!readOnly && (
                  <>
                    {(section.minEntries ?? 1) === 0 &&
                      (!responses[section.key] ||
                        responses[section.key].length === 0) && (
                        <EmptyRepeatableMessage>
                          No entries yet. Press "Add entry" to add one.
                        </EmptyRepeatableMessage>
                      )}
                    <AddEntryBtn
                      type='button'
                      onClick={() => addRepeatableEntry(section.key)}
                      disabled={
                        section.maxEntries &&
                        (responses[section.key]?.length ?? 0) >=
                          section.maxEntries
                      }
                    >
                      + Add entry
                    </AddEntryBtn>
                  </>
                )}
              </div>
            ) : (
              (section.fields || []).map(field => {
                if (!isFieldVisible(field, responses)) return null
                const isAutoPopulated = autoPopulateKeysSet.has(field.key)
                return (
                  <FieldRenderer
                    key={field.key}
                    field={{
                      ...field,
                      required: isFieldRequired(field, responses)
                    }}
                    value={responses[field.key]}
                    commentValue={responses._field_comments?.[field.key]}
                    attachmentsValue={responses._field_attachments?.[field.key]}
                    onAttachmentsChange={handleAttachmentsChange}
                    onChange={handleFieldChange}
                    onCommentChange={handleCommentChange}
                    readOnly={readOnly || isAutoPopulated}
                    autoPopulated={isAutoPopulated}
                    taskId={taskId}
                    task={task}
                    onTaskRefresh={onTaskRefresh}
                    showMissing={showMissing}
                    missingNote={submitAttempted && summary.firstMissingId === field.key ? FIRST_MISSING_NOTE : null}
                  />
                )
              })
            )}
          </SectionCard>
        )
      })}

      {submitAttempted && remaining > 0 && (
        <MissingNotice role={onSubmitBlocked ? undefined : 'alert'}>
          {remaining === 1
            ? '1 required question still needs an answer. It is marked "This question needs an answer" above.'
            : `${remaining} required questions still need an answer. They are marked "This question needs an answer" above.`}
        </MissingNotice>
      )}

      {/* Actions */}
      {!readOnly && (
        <FormActions>
          <SaveDraftBtn type='button' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </SaveDraftBtn>
          {onSubmit && (
            <SubmitBtn
              type='button'
              onClick={requestSubmit}
              disabled={saving}
              $ready={progress === 100}
            >
              {task?.requiresApproval ? 'Submit for approval' : 'Submit'}
            </SubmitBtn>
          )}
        </FormActions>
      )}

      {consentModal && (
        <LocationConsentModal
          onAllow={() => recordLocationConsent('granted')}
          onDecline={() => recordLocationConsent('declined')}
        />
      )}
    </FormContainer>
  )
})

export default FormRenderer

// --- Styled Components ---

const MissingNotice = styled.div`
  margin-top: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  color: #78350f;
  font-size: 15px;
  line-height: 1.45;
`

const FormContainer = styled.div`
  max-width: 800px;
`

const EmptyMessage = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-size: 14px;
`

const ProgressSection = styled.div`
  margin-bottom: 24px;
`

const SaveStatus = styled.div`
  margin-top: 8px;
  min-height: 18px;
`

const SaveStatusText = styled.span`
  font-size: 12px;
  color: ${({ $warning, $success, theme }) =>
    $warning
      ? '#faad14'
      : $success
        ? theme.colors.secondary || '#52c41a'
        : theme.colors.grey?.[800] || '#999'};
`

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const ProgressValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $complete, theme }) =>
    $complete ? theme.colors.secondary : theme.colors.grey?.[800] || '#999'};
`

const ProgressTrack = styled.div`
  height: 8px;
  background-color: ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background-color: ${({ $complete, theme }) =>
    $complete ? theme.colors.secondary : theme.colors.primary};
  border-radius: 4px;
  transition: width 0.3s ease;
`

const SectionCard = styled.div`
  margin-bottom: 24px;
  border: 1px ${({ $conditionallyHidden }) => ($conditionallyHidden ? 'dashed' : 'solid')}
    ${({ theme, $conditionallyHidden }) =>
      $conditionallyHidden ? theme.colors.mediumGrey : theme.colors.lightGrey};
  border-radius: 8px;
  padding: 20px;
  background-color: #fff;
  opacity: ${({ $conditionallyHidden }) => ($conditionallyHidden ? 0.7 : 1)};
`

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ConditionalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
`

const SectionDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const RepeatableEntry = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
`

const RepeatableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const EntryLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const RemoveEntryBtn = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.error || theme.colors.primary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.linen || '#fbede9'};
  }
`

const EmptyRepeatableMessage = styled.p`
  margin: 0 0 12px 0;
  padding: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#666'};
  background-color: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  text-align: center;
`

const AddEntryBtn = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(51, 190, 204, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 16px;
`

const SaveDraftBtn = styled.button`
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const SubmitBtn = styled.button`
  padding: 10px 22px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background-color: #33becc;
  border: 0;
  border-radius: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  min-height: 40px;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background-color: #2aa6b3;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`
